import { useRef, useState } from 'react';

import { Button, showSnackbar } from 'fluxo-ui';
import { DownloadIcon, UploadIcon } from 'fluxo-ui/icons';

import questionService from '../../services/question.service';

interface CSVImporterProps {
    onImportComplete?: (result: any) => void;
    disabled?: boolean;
}

const CSVImporter: React.FC<CSVImporterProps> = ({ onImportComplete, disabled = false }) => {
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDownloadSample = () => {
        try {
            questionService.downloadSampleCSV();
            showSnackbar('Sample CSV downloaded', 'Success', { type: 'success' });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to download sample', 'Error', { type: 'error' });
        }
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.csv')) {
            showSnackbar('Please select a CSV file', 'Invalid File', { type: 'error' });
            return;
        }

        setImporting(true);
        try {
            const result = await questionService.importQuestionsFromCSV(file);
            showSnackbar(`Successfully imported ${result.imported} out of ${result.total} questions`, 'Import Complete', {
                type: 'success',
            });
            onImportComplete?.(result);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to import questions', 'Import Failed', { type: 'error' });
        } finally {
            setImporting(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="space-y-4">
            <div className="p-6 bg-white dark:bg-slate-800 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-center">
                <UploadIcon className="w-12 h-12 mx-auto text-slate-400 dark:text-slate-500 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Import Questions from CSV</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                    Upload a CSV file to bulk import questions. Download the sample file to see the correct format.
                </p>

                <div className="flex items-center justify-center gap-3">
                    <Button
                        label="Download Sample CSV"
                        leftIcon={DownloadIcon}
                        variant="default"
                        layout="outlined"
                        size="sm"
                        onClick={handleDownloadSample}
                        disabled={disabled || importing}
                    />
                    <Button
                        label="Select CSV File"
                        leftIcon={UploadIcon}
                        variant="primary"
                        size="sm"
                        onClick={handleBrowseClick}
                        isLoading={importing}
                        disabled={disabled || importing}
                    />
                </div>

                <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} className="hidden" />
            </div>

            <div className="p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                <h4 className="font-semibold text-teal-900 dark:text-teal-100 mb-2">CSV Format Guidelines:</h4>
                <ul className="text-sm text-teal-800 dark:text-teal-200 space-y-1">
                    <li>
                        • <strong>Required fields:</strong> title, text, type
                    </li>
                    <li>
                        • <strong>Question types:</strong> mcq_single, mcq_multiple, true_false, fill_blank, short_answer, long_answer, code
                    </li>
                    <li>
                        • <strong>Options format:</strong> Option text|true;Option text|false (use semicolon to separate options)
                    </li>
                    <li>
                        • <strong>Tags format:</strong> tag1;tag2;tag3 (use semicolon to separate tags)
                    </li>
                    <li>
                        • <strong>Time format:</strong> Enter total seconds (e.g., 60 for 1 minute)
                    </li>
                    <li>
                        • <strong>Category:</strong> Use category ID from your system
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default CSVImporter;
